/* eslint-disable no-underscore-dangle */
import { useTranslate } from 'hooks/useTranslate';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { store } from 'redux/store';

import { setPreview } from 'modules/_shared/redux/previewActions';
import { getDocuments } from 'modules/documents/redux/documentActions';

import tags from 'modules/_shared/components/EditorWYSIWYG/tags';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { updateDocumentWithValues } from 'modules/_shared/components/EditorWYSIWYG/helpers';

import BoardActPreview from 'modules/boards/components/BoardActPreview';
import BoardActCertificatePreview from 'modules/boards/components/BoardActCertificatePreview';
import DocumentsGenerator from 'modules/documents/components/Creators/DocumentsGenerator';

import useGetTemplateData from '../../../administratives/pages/CouncilDetails/hooks/useGetDocumentData';

const tabs = {
  ACT: 'ACT',
  CERTIFICATE: 'CERTIFICATE',
};

const BoardAct = ({ board }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const [selectedTab, setSelectedTab] = useState(tabs.ACT);
  const [isLoading, setIsLoading] = useState(true);

  const preview = useSelector((state) => state.preview);
  const societyDocuments = useSelector((state) => state.documents);
  const actualSociety = useSelector((state) => state.society?.actualSociety);
  const partnerRepresentants = useSelector(
    (state) => state.partnerRepresentants
  );

  const {
    actTemplate,
    actDocument,
    actCertificateTemplate,
    actCertificateDocument,
  } = board;

  const { data: actDocumentData, isLoading: isLoadingActDocument } =
    useGetTemplateData({
      documentId: actDocument,
      templateId: actTemplate,
    });

  const {
    data: actCertificateDocumentData,
    isLoading: isLoadingActCertificateDocument,
  } = useGetTemplateData({
    documentId: actCertificateDocument,
    templateId: actCertificateTemplate,
  });

  const { document, documentFrame } = actDocumentData;
  const {
    document: certificateDocument,
    documentFrame: certificateDocumentFrame,
  } = actCertificateDocumentData;

  const [actTemplateData, setActTemplateData] = useState();
  const [actCertificateTemplateData, setActCertificateTemplateData] =
    useState();

  const [previewAct, setPreviewAct] = useState();
  const [previewActCertificate, setPreviewActCertificate] = useState();

  const hasActDocument = useMemo(
    () => !!document && !!documentFrame,
    [document, documentFrame]
  );

  const hasActCertificateDocument = useMemo(
    () => !!certificateDocument && !!certificateDocumentFrame,
    [certificateDocument, certificateDocumentFrame]
  );

  const createActPreview = () => {
    const actUpdated = updateDocumentWithValues(
      actTemplateData?.editorModel,
      tags({
        society: actualSociety,
        board,
        partnerRepresentants,
      })
    );
    const actDataToDocument = (
      <DocumentsGenerator
        invitationModel={actUpdated?.html}
        hasConsolidationCalendar={false}
        consolidationCalendar={{}}
      />
    );

    setPreviewAct({
      name: actTemplateData.name,
      bdDocument: actTemplateData,
      document: actDataToDocument,
      documentModel: actUpdated?.html,
      template: actTemplateData?.['_id'],
    });
  };

  const createActCertificatePreview = () => {
    const certificateUpdated = updateDocumentWithValues(
      actCertificateTemplateData?.editorModel,
      tags({
        society: actualSociety,
        board: {
          ...board,
          actTemplateHtml: previewAct?.documentModel || '',
        },
      })
    );
    const certificateDataToDocument = (
      <DocumentsGenerator
        invitationModel={certificateUpdated?.html}
        hasConsolidationCalendar={false}
        consolidationCalendar={{}}
      />
    );

    setPreviewActCertificate({
      name: actCertificateTemplateData.name,
      bdDocument: actCertificateTemplateData,
      document: certificateDataToDocument,
      documentModel: certificateUpdated?.html,
      template: actCertificateTemplateData?.['_id'],
    });
  };

  const handleSelectTabs = async (tab) => {
    setSelectedTab(tab);
  };

  useEffect(() => {
    if (actualSociety) {
      dispatch(getDocuments({ society: actualSociety['_id'] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualSociety]);

  useEffect(() => {
    if (societyDocuments.length) {
      setActTemplateData(
        societyDocuments.find((doc) => doc['_id'] === actTemplate)
      );
      setActCertificateTemplateData(
        societyDocuments.find((doc) => doc['_id'] === actCertificateTemplate)
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societyDocuments]);

  useEffect(() => {
    if (
      actualSociety &&
      actTemplateData &&
      !hasActDocument &&
      !isLoadingActDocument
    ) {
      createActPreview();
    }
  }, [actualSociety, actTemplateData]);

  useEffect(() => {
    if (
      actualSociety &&
      actCertificateTemplateData &&
      !hasActCertificateDocument &&
      !isLoadingActCertificateDocument
    ) {
      createActCertificatePreview();
    }
  }, [actualSociety, actCertificateTemplateData]);

  useEffect(() => {
    if (isLoadingActDocument || isLoadingActCertificateDocument) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [isLoadingActDocument, isLoadingActCertificateDocument]);

  useEffect(() => {
    if (selectedTab === tabs.ACT) {
      store.dispatch(setPreview({ ...previewAct }));
    }

    if (selectedTab === tabs.CERTIFICATE) {
      store.dispatch(setPreview({ ...previewActCertificate }));
    }
  }, [selectedTab, previewAct, previewActCertificate]);

  return (
    <>
      <div className="nk-block pt-4">
        <div className="card">
          <div className="card-aside-wrap">
            <div className="card-content">
              <ul className="nav nav-tabs nav-tabs-mb-icon nav-tabs-card">
                <li className="nav-item">
                  <p
                    className={`nav-link pt-0 ${
                      selectedTab === tabs.ACT ? 'active' : ''
                    }`}
                    onClick={() => handleSelectTabs(tabs.ACT)}
                  >
                    <em className="icon ni ni-file-text" />
                    <span>{t('Act')}</span>
                  </p>
                </li>
                <li className="nav-item">
                  <p
                    className={`nav-link pt-0 ${
                      selectedTab === tabs.CERTIFICATE ? 'active' : ''
                    }`}
                    onClick={() => handleSelectTabs(tabs.CERTIFICATE)}
                  >
                    <em className="icon ni ni-file-text-fill" />
                    <span>{t('Certification')}</span>
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <br />

      {selectedTab === tabs.ACT && preview && !isLoading && (
        <div className="nk-block">
          <BoardActPreview
            board={board}
            hasActDocument={hasActDocument}
            document={document}
            documentFrame={documentFrame}
            actTemplateData={actTemplateData}
          />
        </div>
      )}

      {selectedTab === tabs.CERTIFICATE && preview && !isLoading && (
        <div className="nk-block">
          <BoardActCertificatePreview
            board={board}
            hasActCertificateDocument={hasActCertificateDocument}
            certificateDocument={certificateDocument}
            certificateDocumentFrame={certificateDocumentFrame}
            actCertificateTemplateData={actCertificateTemplateData}
          />
        </div>
      )}

      {isLoading && <CustomLoading />}
    </>
  );
};

export default BoardAct;
